import type { SourceUpdateRequest } from "@/services/sourcesApi";

/**
 * Global scraper defaults from nvg-nexus-api `app/config.py` /
 * `app/scrapers/base/throttle.py`. Shown as placeholders when a source
 * column is null (the engine then uses these).
 */
export const GLOBAL_THROTTLE_DEFAULTS = {
  scraper_batch_size: 10,
  scraper_batch_delay: 2,
  scraper_request_delay: 0.05,
  scraper_max_retries: 3,
} as const;

/** Matches `SourceUpdate` Field(ge=..., le=...) in app/schemas/source.py. */
export const SCRAPER_FIELD_CONSTRAINTS = {
  scraper_batch_size: {
    min: 1,
    max: 50,
    integer: true,
    label: "Batch size",
    placeholder: `default (${GLOBAL_THROTTLE_DEFAULTS.scraper_batch_size})`,
    hint: "Concurrent requests per batch. Empty restores the global default.",
  },
  scraper_batch_delay: {
    min: 0,
    max: 60,
    integer: false,
    label: "Batch delay (seconds)",
    placeholder: `default (${GLOBAL_THROTTLE_DEFAULTS.scraper_batch_delay})`,
    hint: "Seconds to wait between batches. Empty restores the global default.",
  },
  scraper_request_delay: {
    min: 0,
    max: 10,
    integer: false,
    label: "Request delay (seconds)",
    placeholder: `default (${GLOBAL_THROTTLE_DEFAULTS.scraper_request_delay})`,
    hint: "Seconds to wait between individual requests. Empty restores the global default.",
  },
  scraper_max_retries: {
    min: 1,
    max: 10,
    integer: true,
    label: "Max retries",
    placeholder: `default (${GLOBAL_THROTTLE_DEFAULTS.scraper_max_retries})`,
    hint: "Max HTTP retries per request. Empty restores the global default.",
  },
  scrape_interval_hours: {
    min: 6,
    max: 168,
    integer: true,
    label: "Scrape interval (hours)",
    placeholder: "every cron tick",
    hint: "Minimum hours between full scrapes. Empty means every cron tick. Floor is 6h (the cron itself); this can only slow a source down.",
  },
} as const;

export const THROTTLE_PIN_FIELDS = [
  "scraper_batch_size",
  "scraper_batch_delay",
  "scraper_request_delay",
  "scraper_max_retries",
] as const;

export type ThrottlePinField = (typeof THROTTLE_PIN_FIELDS)[number];
export type ScraperNumericField = ThrottlePinField | "scrape_interval_hours";

export interface SourceScraperConfig {
  scraper_batch_size: number | null;
  scraper_batch_delay: number | null;
  scraper_request_delay: number | null;
  scraper_max_retries: number | null;
  scrape_interval_hours: number | null;
  scraper_throttle_auto: boolean;
  scraper_clean_run_streak: number;
}

export interface ScraperConfigForm {
  scraper_batch_size: string;
  scraper_batch_delay: string;
  scraper_request_delay: string;
  scraper_max_retries: string;
  scrape_interval_hours: string;
  scraper_throttle_auto: boolean;
}

const NUMERIC_FIELDS: readonly ScraperNumericField[] = [
  ...THROTTLE_PIN_FIELDS,
  "scrape_interval_hours",
];

export function scraperConfigFromPartial(
  details: Partial<SourceScraperConfig> | null | undefined,
): SourceScraperConfig {
  return {
    scraper_batch_size: details?.scraper_batch_size ?? null,
    scraper_batch_delay: details?.scraper_batch_delay ?? null,
    scraper_request_delay: details?.scraper_request_delay ?? null,
    scraper_max_retries: details?.scraper_max_retries ?? null,
    scrape_interval_hours: details?.scrape_interval_hours ?? null,
    scraper_throttle_auto: details?.scraper_throttle_auto ?? true,
    scraper_clean_run_streak: details?.scraper_clean_run_streak ?? 0,
  };
}

export function scraperConfigToForm(
  config: SourceScraperConfig,
): ScraperConfigForm {
  return {
    scraper_batch_size: nullableNumberToInput(config.scraper_batch_size),
    scraper_batch_delay: nullableNumberToInput(config.scraper_batch_delay),
    scraper_request_delay: nullableNumberToInput(config.scraper_request_delay),
    scraper_max_retries: nullableNumberToInput(config.scraper_max_retries),
    scrape_interval_hours: nullableNumberToInput(config.scrape_interval_hours),
    scraper_throttle_auto: config.scraper_throttle_auto,
  };
}

export function nullableNumberToInput(
  value: number | null | undefined,
): string {
  if (value == null) return "";
  return String(value);
}

export function parseOptionalNumber(
  raw: string,
  field: ScraperNumericField,
): { value: number | null } | { error: string } {
  const constraint = SCRAPER_FIELD_CONSTRAINTS[field];
  const trimmed = raw.trim();
  if (trimmed === "") return { value: null };

  const numeric = Number(trimmed);
  if (!Number.isFinite(numeric)) {
    return { error: `${constraint.label} must be a number` };
  }
  if (constraint.integer && !Number.isInteger(numeric)) {
    return { error: `${constraint.label} must be a whole number` };
  }
  if (numeric < constraint.min || numeric > constraint.max) {
    return {
      error: `${constraint.label} must be between ${constraint.min} and ${constraint.max}`,
    };
  }
  return { value: numeric };
}

export function numbersEqual(
  a: number | null | undefined,
  b: number | null | undefined,
): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return a === b;
}

/**
 * Auto-tune switch value the operator will actually persist.
 *
 * Editing a throttle field without touching the switch pins auto-tuning
 * off — same rule as PATCH /sources/{id}. Turning the switch back on
 * is an explicit re-arm.
 */
export function effectiveThrottleAuto(args: {
  originalAuto: boolean;
  formAuto: boolean;
  autoTouched: boolean;
  throttleFieldsChanged: boolean;
}): boolean {
  if (args.autoTouched) return args.formAuto;
  if (args.throttleFieldsChanged) return false;
  return args.originalAuto;
}

export type ScraperConfigPatchResult =
  | { ok: true; patch: SourceUpdateRequest; throttleFieldsChanged: boolean }
  | { ok: false; errors: Partial<Record<ScraperNumericField, string>> };

export function buildScraperConfigPatch(
  original: SourceScraperConfig,
  form: ScraperConfigForm,
  autoTouched: boolean,
): ScraperConfigPatchResult {
  const errors: Partial<Record<ScraperNumericField, string>> = {};
  const parsed: Record<ScraperNumericField, number | null> = {
    scraper_batch_size: null,
    scraper_batch_delay: null,
    scraper_request_delay: null,
    scraper_max_retries: null,
    scrape_interval_hours: null,
  };

  for (const field of NUMERIC_FIELDS) {
    const result = parseOptionalNumber(form[field], field);
    if ("error" in result) {
      errors[field] = result.error;
    } else {
      parsed[field] = result.value;
    }
  }

  if (Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }

  const patch: SourceUpdateRequest = {};
  for (const field of NUMERIC_FIELDS) {
    if (!numbersEqual(parsed[field], original[field])) {
      patch[field] = parsed[field];
    }
  }

  const throttleFieldsChanged = THROTTLE_PIN_FIELDS.some(
    (field) => field in patch,
  );
  const auto = effectiveThrottleAuto({
    originalAuto: original.scraper_throttle_auto,
    formAuto: form.scraper_throttle_auto,
    autoTouched,
    throttleFieldsChanged,
  });

  if (throttleFieldsChanged) {
    // Always send auto with a throttle edit so the UI switch is the
    // source of truth (no silent API-side pin that the operator cannot see).
    patch.scraper_throttle_auto = auto;
  } else if (auto !== original.scraper_throttle_auto) {
    patch.scraper_throttle_auto = auto;
  }

  return { ok: true, patch, throttleFieldsChanged };
}

export function isEmptyPatch(patch: SourceUpdateRequest): boolean {
  return Object.keys(patch).length === 0;
}

export function formatScraperCadence(hours: number | null | undefined): string {
  if (hours == null) return "every cron tick";
  return `every ${hours}h`;
}

export function formatThrottleSummary(config: SourceScraperConfig): string {
  const batch =
    config.scraper_batch_size == null
      ? "default"
      : String(config.scraper_batch_size);
  const delay =
    config.scraper_batch_delay == null
      ? "default"
      : `${config.scraper_batch_delay}s`;
  const request =
    config.scraper_request_delay == null
      ? "default"
      : `${config.scraper_request_delay}s/req`;
  const retries =
    config.scraper_max_retries == null
      ? "default"
      : String(config.scraper_max_retries);
  return `batch ${batch} · delay ${delay} · ${request} · retries ${retries}`;
}

export function isThrottleAtFloor(
  batchSize: number | null | undefined,
): boolean {
  return batchSize === 1;
}
